import { apiGet, apiPost, ApiError } from './client';

/**
 * One-time service fees (email setup, GBP claim, migrations).
 *
 * These use authorize-then-capture: confirming places a HOLD on the card and
 * collects nothing. Payment is taken only once the work is delivered. The
 * wording throughout reflects that — some card statements show a hold
 * identically to a settled payment, so a customer who is not told plainly will
 * believe they have already been charged.
 */

export interface ServiceCharge {
  id: string;
  service_type: string;
  description: string;
  amount_cents: number;
  currency: string;
  status:
    | 'draft'
    | 'authorized'
    | 'delivered'
    | 'captured'
    | 'expired'
    | 'voided'
    | 'failed'
    | 'refunded';
  status_label: string;
  authorized_at: string | null;
  authorization_expires_at: string | null;
  captured_at: string | null;
  captured_amount_cents: number | null;
  created_at: string;
}

export interface AuthorizeResponse {
  data: ServiceCharge | null;
  message?: string;
}

/**
 * Returned when the card issuer demands 3D Secure. Not an error — the customer
 * must complete the challenge, after which a webhook confirms the hold.
 */
export interface RequiresActionResponse {
  requires_action: true;
  client_secret: string;
  message: string;
}

export async function listServiceCharges(): Promise<ServiceCharge[]> {
  const response = await apiGet<{ data: ServiceCharge[] }>('/api/customer/service-charges');
  return response.data;
}

/**
 * Confirm a charge, placing the hold.
 *
 * Throws `RequiresActionError` when 3D Secure is needed so the caller can hand
 * the client secret to Stripe.js — the API signals this with a 402 rather than
 * a success body.
 */
export async function authorizeServiceCharge(chargeId: string): Promise<ServiceCharge | null> {
  try {
    const response = await apiPost<AuthorizeResponse>(
      `/api/customer/service-charges/${chargeId}/authorize`
    );
    return response.data;
  } catch (err) {
    // A 402 carrying a client secret is the 3DS signal. A 402 without one is
    // an ordinary decline and propagates as a normal error.
    if (err instanceof ApiError && err.status === 402) {
      const body = err.body as Partial<RequiresActionResponse> | undefined;
      if (body?.requires_action && body.client_secret) {
        throw new RequiresActionError(
          body.client_secret,
          body.message || 'Additional verification required.'
        );
      }
    }
    throw err;
  }
}

export class RequiresActionError extends Error {
  constructor(
    public clientSecret: string,
    message: string
  ) {
    super(message);
    this.name = 'RequiresActionError';
  }
}

export function formatAmount(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/** Days until a hold lapses, or null when there is no deadline. */
export function daysUntilExpiry(expiresAt: string | null): number | null {
  if (!expiresAt) return null;
  const ms = Date.parse(expiresAt) - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / 86_400_000));
}
